import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { WidgetService } from '../shared/services/widget.service';
import { WidgetSimpleInfo } from '../campaign/widgets.model';
import { CurrentSiteIdStore } from '../core/current-site-id.store';
import { TargetSite } from '../core/target-site';
import { AgentMentionOption } from './agent-mention.model';

// Supplies campaigns for @-mention autocomplete. Lazily loaded on the first mention and cached per
// site (the assistant is global; a site switch invalidates the cache). Mirrors the audience/session
// condition picker's source: the whole /simple-widgets list, de-duplicated to one row per campaign
// and filtered client-side. Failures degrade to an empty list (mentions just won't autocomplete).
@Injectable({ providedIn: 'root' })
export class AgentCampaignSource {
  private readonly widgets = inject(WidgetService);
  private readonly currentSiteIdStore = inject(CurrentSiteIdStore);
  private readonly translate = inject(TranslateService);

  private cache: AgentMentionOption[] | null = null;
  private cacheSiteId: number | undefined;
  private inFlight: Promise<AgentMentionOption[]> | null = null;

  campaigns(): Promise<AgentMentionOption[]> {
    const site = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (this.cache && this.cacheSiteId === site) {
      return Promise.resolve(this.cache);
    }
    if (this.inFlight && this.cacheSiteId === site) {
      return this.inFlight;
    }
    this.cacheSiteId = site;
    this.inFlight = this.load();
    return this.inFlight;
  }

  private async load(): Promise<AgentMentionOption[]> {
    try {
      const widgets = await firstValueFrom(this.widgets.getAllWidgetsSimpleInfo());
      this.cache = dedupeToCampaigns(widgets, this.translate);
      return this.cache;
    } catch {
      this.cache = null;
      return [];
    } finally {
      this.inFlight = null;
    }
  }
}

// /simple-widgets is widget-level (many per campaign); collapse to the first widget seen per
// campaignId. A widget name like "Variant 1" can't tell campaigns apart, so it is never the label.
// Status uses the shared `campaign.status-<X>` keys so the hint is localized, not a raw enum value.
function dedupeToCampaigns(widgets: readonly WidgetSimpleInfo[], translate: TranslateService): AgentMentionOption[] {
  const byId = new Map<number, AgentMentionOption>();
  for (const widget of widgets) {
    const id = widget.campaignId;
    if (id == null || byId.has(id)) {
      continue;
    }
    const name = widget.campaignName?.trim();
    const label = name || translate.instant('agent.mention.unnamed-campaign', { id });
    const status = widget.campaignStatus != null ? translate.instant(`campaign.status-${widget.campaignStatus}`) : '';
    const hint = [name ? `#${id}` : '', status].filter((part) => part.length > 0).join(' · ');
    byId.set(id, { kind: 'campaign', id, label, searchText: label.toLowerCase(), hint });
  }
  return [...byId.values()];
}
